/**
 * Route guards for authentication and authorization.
 *
 * AuthGuard: Redirects unauthenticated users to /login.
 *            Shows a spinner while the initial auth check runs.
 * AdminGuard: Extends AuthGuard with admin role check.
 *
 * Cross-References:
 *   - FR-AUTH-001: Authentication required for all protected routes
 *   - FR-RBAC-003: Role-based access control
 */
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';

export function AuthGuard() {
  const { isAuthenticated, isLoading } = useAuthStore();
  const location = useLocation();

  // Still checking for a valid refresh cookie → show loader
  if (isLoading) {
    return (
      <div className="page-loader">
        <div className="page-loader__spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <Outlet />;
}

/** Place inside AuthGuard; HR access does not require general user-admin rights. */
export function PermissionGuard({ permission }: { permission: string }) {
  const allowed = useAuthStore((state) => state.user?.permissions.includes(permission));
  return allowed ? <Outlet /> : <Navigate to="/dashboard" replace />;
}

export function AdminGuard() {
  const { isAuthenticated, isLoading, user } = useAuthStore();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="page-loader">
        <div className="page-loader__spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const isAdmin =
    user?.permissions?.some((p) =>
      ['users.read.org', 'users.manage.org', 'roles.manage.org', 'tenants.manage.org', 'integrations.manage.org', 'modules.manage.org', 'audit.read.org'].includes(p),
    ) ?? false;
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
}
